import { ActionIcon, Group, Tooltip, CopyButton } from "@mantine/core"
import {
  IconBrandTwitter,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconMail,
  IconLink,
  IconCheck,
} from "@tabler/icons-react"
import { notifications } from "@mantine/notifications"

export interface ShareButtonsProps {
  url?: string
  title?: string
  text?: string
  size?: "sm" | "md" | "lg"
  variant?: "default" | "subtle" | "filled"
  orientation?: "horizontal" | "vertical"
}

const DEFAULT_TITLE = "Release The Epstein Files Now"
const DEFAULT_TEXT =
  "Demanding complete transparency in the Jeffrey Epstein case. View court documents, timeline, and take action."
const DEFAULT_URL = "https://rtefn.com"

export function ShareButtons({
  url = DEFAULT_URL,
  title = DEFAULT_TITLE,
  text = DEFAULT_TEXT,
  size = "md",
  variant = "default",
  orientation = "horizontal",
}: ShareButtonsProps) {
  const encodedUrl = encodeURIComponent(url)
  const encodedText = encodeURIComponent(text)
  const encodedTitle = encodeURIComponent(title)

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=EpsteinFiles,Transparency,Justice`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
  }

  const handleShare = (platform: string) => {
    const link = shareLinks[platform as keyof typeof shareLinks]
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer,width=600,height=400")
    }
  }

  const iconSize = size === "sm" ? 16 : size === "md" ? 20 : 24

  return (
    <Group gap={orientation === "horizontal" ? "xs" : "xs"} className={orientation === "vertical" ? "flex-col" : ""}>
      <Tooltip label="Share on Twitter" position="top">
        <ActionIcon
          variant={variant}
          size={size}
          color="blue"
          onClick={() => handleShare("twitter")}
          aria-label="Share on Twitter"
        >
          <IconBrandTwitter size={iconSize} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Share on Facebook" position="top">
        <ActionIcon
          variant={variant}
          size={size}
          color="blue"
          onClick={() => handleShare("facebook")}
          aria-label="Share on Facebook"
        >
          <IconBrandFacebook size={iconSize} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Share on LinkedIn" position="top">
        <ActionIcon
          variant={variant}
          size={size}
          color="blue"
          onClick={() => handleShare("linkedin")}
          aria-label="Share on LinkedIn"
        >
          <IconBrandLinkedin size={iconSize} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Share via Email" position="top">
        <ActionIcon
          variant={variant}
          size={size}
          color="blue"
          onClick={() => handleShare("email")}
          aria-label="Share via Email"
        >
          <IconMail size={iconSize} />
        </ActionIcon>
      </Tooltip>

      <CopyButton value={url} timeout={2000}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied!" : "Copy Link"} position="top">
            <ActionIcon
              variant={variant}
              size={size}
              color={copied ? "green" : "blue"}
              onClick={() => {
                copy()
                notifications.show({
                  title: "Link Copied!",
                  message: "Share link copied to clipboard",
                  color: "green",
                  icon: <IconCheck size={16} />,
                })
              }}
              aria-label="Copy link"
            >
              {copied ? <IconCheck size={iconSize} /> : <IconLink size={iconSize} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  )
}
